import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { toast } from "sonner";
import { Bank, CreditCard, PaperPlaneTilt, ArrowLeft, ArrowSquareOut } from "@phosphor-icons/react";
import Header from "../components/Header";

function fmtTs(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function Transactions() {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("all");

  const KIND_META = {
    direct_deposit:  { icon: Bank, label: t("tx.kind.direct_deposit"), tone: "text-[#184A30]" },
    card_topup:      { icon: CreditCard, label: t("tx.kind.card_topup"), tone: "text-[#7B5418]" },
    outbound_payout: { icon: PaperPlaneTilt, label: t("tx.kind.outbound_payout"), tone: "text-[#4F7BA6]" },
  };

  const STATUS_TONE = {
    "Delivered": "bg-emerald-100 text-emerald-800",
    "Loaded": "bg-emerald-100 text-emerald-800",
    "Wired to receiver": "bg-emerald-100 text-emerald-800",
    "Completed": "bg-emerald-100 text-emerald-800",
    "Approved": "bg-[#184A30]/10 text-[#184A30]",
    "Paid · wire pending": "bg-blue-100 text-blue-800",
    "Payout in flight": "bg-amber-100 text-amber-800",
    "Payment due": "bg-blue-100 text-blue-800",
    "Pending": "bg-amber-100 text-amber-800",
    "Awaiting admin review": "bg-amber-100 text-amber-800",
    "Rejected": "bg-rose-100 text-rose-800",
    "Failed": "bg-rose-100 text-rose-800",
    "Payout failed": "bg-rose-100 text-rose-800",
    "Payout returned": "bg-rose-100 text-rose-800",
    "Expired": "bg-stone-100 text-stone-600",
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/transactions?limit=200");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("tx.toast.load_failed"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const items = (data?.items || []).filter((it) =>
    kindFilter === "all" ? true : it.kind === kindFilter,
  );

  const byKind = data?.by_kind || {};
  const totalByCcy = data?.total_outflow_by_currency || {};

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <header className="border-b border-[#E5E0D8] bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/dashboard" className="text-[#4A5C50] hover:text-[#184A30]" data-testid="tx-back">
            <ArrowLeft size={18} />
          </Link>
          <h1 className="serif text-2xl text-[#112318]">{t("tx.heading")}</h1>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-8 space-y-5" data-testid="tx-page">
        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3" data-testid="tx-summary">
          <SummaryCard label={t("tx.summary.deposits")} value={byKind.direct_deposit || 0} sub={Object.entries(totalByCcy).map(([k,v])=>`$${v.toFixed(2)} ${k}`).join(" · ") || "—"} testid="tx-sum-dd" />
          <SummaryCard label={t("tx.summary.topups")} value={byKind.card_topup || 0} sub={byKind.card_topup ? t("tx.summary.topups_sub") : "—"} testid="tx-sum-topup" />
          <SummaryCard label={t("tx.summary.payouts")} value={byKind.outbound_payout || 0} sub={byKind.outbound_payout ? t("tx.summary.payouts_sub") : "—"} testid="tx-sum-payout" />
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-1.5 text-[11px] tracking-[0.18em] uppercase" data-testid="tx-filters">
          {[
            { k: "all", label: `${t("tx.filter.all")} (${data?.count ?? 0})` },
            { k: "direct_deposit", label: `${t("tx.filter.deposits")} (${byKind.direct_deposit || 0})` },
            { k: "card_topup", label: `${t("tx.filter.topups")} (${byKind.card_topup || 0})` },
            { k: "outbound_payout", label: `${t("tx.filter.payouts")} (${byKind.outbound_payout || 0})` },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setKindFilter(f.k)}
              className={`px-3 py-1.5 rounded-full border transition ${
                kindFilter === f.k
                  ? "bg-[#184A30] text-white border-[#184A30]"
                  : "border-[#E5E0D8] text-[#4A5C50] hover:border-[#184A30]"
              }`}
              data-testid={`tx-filter-${f.k}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading && <p className="text-sm text-[#4A5C50]" data-testid="tx-loading">{t("tx.loading")}</p>}

        {!loading && items.length === 0 && (
          <div className="rounded-2xl border border-[#E5E0D8] bg-white p-8 text-center" data-testid="tx-empty">
            <p className="text-sm text-[#4A5C50]">
              {kindFilter !== "all" ? t("tx.empty.filtered") : t("tx.empty.none")}
            </p>
            <Link to="/direct-deposits" className="text-[11px] tracking-[0.2em] uppercase text-[#184A30] hover:underline mt-3 inline-block">
              {t("tx.empty.cta")}
            </Link>
          </div>
        )}

        {!loading && items.length > 0 && (
          <ul className="divide-y divide-[#E5E0D8] border border-[#E5E0D8] rounded-2xl bg-white" data-testid="tx-list">
            {items.map((it) => {
              const meta = KIND_META[it.kind] || { icon: Bank, label: it.kind, tone: "text-stone-600" };
              const Icon = meta.icon;
              const statusTone = STATUS_TONE[it.status_label] || "bg-stone-100 text-stone-600";
              return (
                <li
                  key={`${it.kind}-${it.id}`}
                  className="flex items-start gap-3 p-4"
                  data-testid={`tx-row-${it.kind}-${it.ref || it.id.slice(0, 8)}`}
                >
                  <Icon size={22} weight="duotone" className={`${meta.tone} shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="serif font-semibold text-[#112318]">
                        ${Number(it.amount).toFixed(2)} {it.currency}
                      </p>
                      <span className={`text-[10px] mono px-1.5 py-0.5 rounded ${statusTone}`} data-testid={`tx-status-${it.ref || it.id.slice(0, 8)}`}>
                        {it.status_label}
                      </span>
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">
                        {meta.label}
                      </span>
                    </div>
                    <p className="text-[12px] text-[#4A5C50] mt-0.5 truncate">{it.summary}</p>
                    <p className="text-[10px] mono text-[#4A5C50] mt-0.5">
                      {it.ref ? `${it.ref} · ` : ""}{fmtTs(it.ts)}
                    </p>
                  </div>
                  {it.link && (
                    <Link
                      to={it.link}
                      className="text-[#184A30] hover:text-[#0F3320] shrink-0"
                      data-testid={`tx-open-${it.ref || it.id.slice(0, 8)}`}
                      title={t("tx.open_title")}
                    >
                      <ArrowSquareOut size={18} />
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}

function SummaryCard({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-[#E5E0D8] bg-white p-3" data-testid={testid}>
      <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">{label}</p>
      <p className="serif text-2xl text-[#112318] mt-0.5">{value}</p>
      <p className="text-[10px] mono text-[#4A5C50] mt-0.5 truncate">{sub}</p>
    </div>
  );
}
